import { type FC } from 'react';
import { useDispatch } from 'react-redux';
import { BsX } from 'react-icons/bs';
import { Form } from 'react-bootstrap';
import { Draggable } from 'react-beautiful-dnd';

import { type Poll, type PollChoice } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';
import { pollChoiceToPollChoicePropeties } from '@editor2/mappers/element';
import { pollChoice } from './styles';
interface Props {
	element: Poll;
	choice: PollChoice;
	courseId: number;
	index: number;
}

const MAX_CHARS_NUMBER = 20;

const PollChoiceElement: FC<Props> = (props) => {
	const { element, choice, courseId, index } = props;

	const commonPayload = { elementId: element.family_id, courseId };

	const [pollChoiceProperties, handleLocalChange] = useDebounceProtectedState(
		choice,
		pollChoiceToPollChoicePropeties
	);

	const shortenedBodyLength = pollChoiceProperties.shortened_body?.length || 0;

	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	const handleDebouncedChange = (value) => {
		const choices = [
			...element.choices.slice(0, index),
			{ ...choice, ...value },
			...element.choices.slice(index + 1)
		];

		handleLocalChange(value);
		debouncedDispatch(updateElement({ ...commonPayload, choices }));
	};

	const handleDeleteChoiceClick = (index: number) => {
		if (window.confirm(`Are you sure to delete the choice?`)) {
			dispatch(
				updateElement({
					...commonPayload,
					choices: [...element.choices.slice(0, index), ...element.choices.slice(index + 1)],
					...(element.data && {
						data: [...element.data.slice(0, index + 1), ...element.data.slice(index + 2)]
					})
				})
			);
		}
	};

	return (
		<Draggable draggableId={choice.family_id} index={index}>
			{(provided) => (
				<div ref={provided.innerRef} {...provided.draggableProps} className={pollChoice}>
					<Form.Group>
						<div className="choiceRow">
							<div className="choice" {...provided.dragHandleProps}>
								<Form.Control
									value={pollChoiceProperties.body}
									placeholder="Choice"
									onChange={(e) => handleDebouncedChange({ body: e.target.value })}
								/>
							</div>

							<Form.Control
								value={pollChoiceProperties.shortened_body}
								placeholder="Shortened choice"
								className="shortenedChoice"
								onChange={(e) => handleDebouncedChange({ shortened_body: e.target.value })}
							/>
						</div>
						<Form.Text
							className={`charCount ${shortenedBodyLength > 20 && 'overflow'} text-muted `}>
							{MAX_CHARS_NUMBER - shortenedBodyLength} characters left
						</Form.Text>
						<BsX className="deleteChoiceIcon" onClick={() => handleDeleteChoiceClick(index)} />
					</Form.Group>
				</div>
			)}
		</Draggable>
	);
};

export default PollChoiceElement;
